import { PALMSModule, PlaylistContentDownloadDto, PostDownloadDto } from "collaboration-service";
import SafeHTML from "components/SafeHTML/SafeHTML";
import { Icon, ImgIcons, LottieIcon } from "imaginarity-react-ui";
import * as React from "react";
import { ApplicationState, useAppSelector } from "services/ApplicationState/ApplicationState";
import { initPostDownloadDto, shallowCompare } from "services/Helpers";
import { windowOpen } from "services/Helpers/LinkHelpers";
import { sanitizedNothingAllowedAttributes } from "services/Helpers/SanitizedHelper";
import { useImgI18N } from "services/ImgI18N";
import { getPlaylistContentData } from "../Edit/PlaylistEditContentImplementedElement";
import { PlaylistDetailNodeItemProps, useHidePlaylistNodeItem } from "../PlaylistDetailNodeItem";
import { PlaylistDetailSC as T } from "../PlaylistDetailSC";

const mapper = (s: ApplicationState) => ({
    contentLanguage: s.contentLanguage,
    filter: s.playlistsState.filter,
    params: s.params,
});

const PALMSPlaylistNodeItem = (p: PlaylistDetailNodeItemProps<PostDownloadDto> & { content: PlaylistContentDownloadDto }) => {
    const { item, state, playlistContent, isOptional, nodeReference,
        transition, warning, isMobile, nodeIsDone, content, palmsLinks } = p;
    const { contentLanguage } = useAppSelector(mapper, shallowCompare);
    const { t, currentLanguage } = useImgI18N("playlist");
    const lng = contentLanguage ?? currentLanguage;

    const dataStr = content?.referenceData;
    const data = React.useMemo(() => {
        return dataStr ? JSON.parse(dataStr) as PALMSModule : undefined
    }, [dataStr]);

    const post = React.useMemo(() => {
        return initPostDownloadDto("", "PALMS", data?.id)
    }, [data?.id]);

    // console.log("PALMSPlaylistNodeItem.tsx::32 => data", data);

    const { icon, title } = React.useMemo(() => {
        return getPlaylistContentData(content, lng, post);
    }, [content, lng, post]);


    const link = (palmsLinks ?? {})[item.id];

    //const link = data?.startLink;
    //console.log("PALMSPlaylistNodeItem.tsx::49 => link", link);

    // const emptyImageUrl = useThemePart(t => t.emptyImageUrl);
    // const image = getLink(item.media, "self")?.uri ?? emptyImageUrl;
    const image = "https://cdn.imaginarity.com/public/images/palms/palmsV02.png";
    const elementIsReference = item.id === nodeReference;
    //const link = _.find(data?.link, l => l.rel === "alternate")?.href ?? "";
    //"https://ppn.porsche.com/palms_nts/nd/fresco/repository/EKP000290270.html?module=AGWZZYXXY-2024-002%0d"
    const openLink = React.useMemo(() => () => {
        if (link)
            windowOpen(link, "_blank");
    }, [link]);

    const result = useHidePlaylistNodeItem(p);
    if (result.hide && data)
        return null;


    return (
        <T.PLItemContainer isMobile={isMobile} isPreview={p.preview ?? false}>
            <T.PLItemImage imageUrl={image} isMobile={isMobile} highlighted={elementIsReference} color={warning ? "@warning" : "@accentRed"} onClick={openLink} >
                {link === undefined &&
                    <div style={{ position: "absolute", top: "50%", right: "45%" }}>
                        <LottieIcon
                            url={"images/lottiIcons/spinner.json"}
                            speed={1}
                            autoplay
                            loop
                            height={40}
                            width={40}
                        />
                    </div>
                }
            </T.PLItemImage>
            <T.PLItemContainerContent isMobile={isMobile} onClick={openLink} title={link}>
                <T.PLItemContainerTitle isMobile={isMobile}>
                    {elementIsReference &&
                        <T.PLItemContainerTitleDot color={warning ? "@warning" : "@accentRed"} />
                    }
                    <SafeHTML html={title} allowedTags={[]} allowedAttributes={{}} />
                </T.PLItemContainerTitle>
                {!isMobile &&
                    <T.PLItemContainerDescription>
                        {item.descriptions &&
                            // <SafeHTML html={getTranslated(item.descriptions, contentLanguage).text ?? ""} allowedTags={sanitizedAllowedTagsMinimumSafety} allowedAttributes={sanitizedNothingAllowedAttributes} />
                            <SafeHTML
                                html={data?.description ?? ""}
                                allowedTags={["strong", "span", "b", "i", "u"]}
                                allowedAttributes={sanitizedNothingAllowedAttributes}
                            />
                        }
                    </T.PLItemContainerDescription>
                }
                <T.PLItemContainerBottomCat isMobile={isMobile}>
                    <Icon name={icon} marginTop={3} style={{ float: "left", marginLeft: -2, marginRight: 10 }} size="16px" />
                    {item.type}
                    {playlistContent?.duration &&
                        <>
                            <span style={{ padding: "0 10px" }}>&bull;</span>
                            {`${Math.floor(playlistContent.duration * 60 * 100) / 100} Min`}
                        </>
                    }
                </T.PLItemContainerBottomCat>
            </T.PLItemContainerContent>
            {p.preview !== true &&
                <T.PLItemContainerRight isMobile={isMobile} onClick={openLink}>
                    {isOptional ?
                        <T.ColoredDiv color={state.state === "Finished" ? "@accentGreen" : "@lightGrey"}>
                            <div style={{ float: "right" }}>{state.state === "Finished" ? t("done") : t("optional")}</div>
                            <Icon name={state.state === "Finished" ? "check circle" : "empty"} style={{ float: "right", marginRight: 5 }} marginTop={4} color={state.state === "Finished" ? "@accentGreen" : "inherit"} />
                        </T.ColoredDiv>
                        :
                        (transition === "ReferenceDone" || transition === "ReferenceDoneWithValue") ?
                            elementIsReference ?
                                <T.ColoredDiv color={(result.text === "100%" || nodeIsDone) ? "@accentGreen" : warning ? "@warning" : result.color}>
                                    <div style={{ float: "right" }}>
                                        {(result.text === "100%" || nodeIsDone) ? t("done") : warning ? t("warning") : t(result.text)}
                                    </div>
                                    <Icon name={(result.text === "100%" || nodeIsDone) ? "check circle" : warning ? "exclamation triangle" : result.icon as ImgIcons} style={{ float: "right", marginRight: 5 }} marginTop={4} color={(result.text === "100%" || nodeIsDone) ? "@accentGreen" : warning ? "@warning" : result.color} />
                                </T.ColoredDiv>
                                :
                                <T.ColoredDiv color={result.text === "100%" ? "@accentGreen" : "@middleLightGrey"}>
                                    <div style={{ float: "right" }}>{result.text === "100%" ? t("done") : t("optional")}</div>
                                    <Icon name="empty" style={{ float: "right", marginRight: 5 }} marginTop={4} />
                                </T.ColoredDiv>
                            :
                            <T.ColoredDiv color={result.text === "100%" ? "@accentGreen" : warning ? "@warning" : result.color}>
                                <div style={{ float: "right" }}>
                                    {result.text === "100%" ? t("done") : t(result.text)}
                                </div>
                                <Icon
                                    name={result.icon as ImgIcons}
                                    style={{ float: "right", marginRight: 5 }}
                                    marginTop={4}
                                    color={result.text === "100%" ? "@accentGreen" : warning ? "@warning" : result.color}
                                />
                            </T.ColoredDiv>
                    }
                </T.PLItemContainerRight>
            }
            {/*showModal && <PlaylistContentDimmer
                playlistStateId={p.playlistStateId}
                item={item}
                link={link ?? ""}
                result={result}
                playlistId={plId as string}
                isOptional={isOptional ?? false}
                transition={transition}
                elementIsReference={elementIsReference}
                nodeIsDone={nodeIsDone ?? false}
                warning={warning ?? false}
                state={state}
                closeDimmer={togglePreview}
                loadPlaylistStates={loadPlaylistStates}
            />
            */}
        </T.PLItemContainer>
    );
}
export default PALMSPlaylistNodeItem;